import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Bus, Plus, Trash2, Send, CalendarClock, X, Loader2, ArrowLeft, Printer, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { api, formatDate, needsTransfer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-white/5 text-gray-400 border-white/15" },
  sent: { label: "Sent to operator", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};

export default function AdminOperations() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cfg, setCfg] = useState({ dates: [], buggy_slots: [] });
  const [newVehicle, setNewVehicle] = useState({ name: "", time: "", capacity: "" });
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [change, setChange] = useState(null); // booking being changed
  const [changeForm, setChangeForm] = useState({ date: "", slot: "", message: "" });
  const [sendingChange, setSendingChange] = useState(false);
  const [opEmail, setOpEmail] = useState("");
  const [opDate, setOpDate] = useState("");
  const [sendingOp, setSendingOp] = useState(false);

  const loadAll = () => {
    api.get("/admin/bookings").then((r) => {
      const paidTours = (r.data.tour_bookings || []).filter((b) => b.payment_status === "paid" && needsTransfer(b));
      setBookings(paidTours);
    }).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data.vehicles || [])).catch(() => {});
  };

  useEffect(() => {
    loadAll();
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const addVehicle = async () => {
    if (!newVehicle.name.trim() || !newVehicle.time.trim()) return toast.error("Enter a vehicle name and time");
    setAddingVehicle(true);
    try {
      await api.post("/admin/vehicles", { name: newVehicle.name, time: newVehicle.time, capacity: parseInt(newVehicle.capacity) || 0 });
      setNewVehicle({ name: "", time: "", capacity: "" });
      loadAll();
      toast.success("Vehicle added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add vehicle");
    } finally {
      setAddingVehicle(false);
    }
  };

  const removeVehicle = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? Any guests on it will be unassigned.`)) return;
    try {
      await api.delete(`/admin/vehicles/${v.id}`);
      loadAll();
      toast.success("Vehicle deleted");
    } catch (e) {
      toast.error("Could not delete vehicle");
    }
  };

  const guestsOf = (b) => (b.adults || 0) + (b.children || 0) || 1;
  const seatsUsed = (vehicleId) => bookings.filter((b) => b.vehicle_id === vehicleId).reduce((sum, b) => sum + guestsOf(b), 0);

  const setOps = async (b, patch) => {
    // Capacity guard: warn before assigning a guest that would exceed a vehicle's seats
    if (patch.vehicle_id) {
      const v = vehicles.find((x) => x.id === patch.vehicle_id);
      if (v && v.capacity > 0) {
        const usedExcl = seatsUsed(v.id) - (b.vehicle_id === v.id ? guestsOf(b) : 0);
        if (usedExcl + guestsOf(b) > v.capacity) {
          if (!window.confirm(`${v.name} only has ${v.capacity} seats and would go to ${usedExcl + guestsOf(b)}. Assign anyway?`)) return;
        }
      }
    }
    try {
      const r = await api.patch(`/admin/bookings/${b.id}/operations`, patch);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? r.data.booking : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const [opsReport, setOpsReport] = useState(null); // {type, data}
  const runDeparture = async (onlyIds) => {
    setSendingAll(true);
    try {
      const r = await api.post("/admin/departure-info/send-all", onlyIds ? { only_ids: onlyIds } : {});
      setOpsReport({ type: "departure", data: r.data });
      if (r.data.failed > 0) toast.error(`${r.data.sent} sent · ${r.data.failed} failed — see the list`);
      else toast.success(`Departure info sent to ${r.data.sent} guest(s)`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSendingAll(false);
    }
  };
  const sendAll = async () => {
    const assigned = bookings.filter((b) => b.vehicle_id).length;
    if (assigned === 0) return toast.error("Assign at least one guest to a vehicle first");
    if (!window.confirm(`Send departure info (with QR pass) to ${assigned} assigned guest(s)?`)) return;
    runDeparture(null);
  };
  const resendDepartureFailed = () => {
    const ids = (opsReport?.data?.results || []).filter((r) => r.status === "failed").map((r) => r.id);
    if (ids.length) runDeparture(ids);
  };

  const openChange = (b) => {
    const defaultMsg = `Dear ${(b.customer.full_name || "").split(" ")[0] || "guest"}, we've had to move your ${b.tour_name} tour to another slot. Does this work for you? Please confirm using the buttons below. Thank you for your understanding.`;
    setChangeForm({ date: b.date, slot: b.slot || "", message: defaultMsg });
    setChange(b);
  };

  const sendChange = async () => {
    setSendingChange(true);
    try {
      const body = { message: changeForm.message, new_date: changeForm.date };
      if (change.type === "buggy") body.new_slot = changeForm.slot;
      await api.post(`/admin/bookings/${change.id}/propose-change`, body);
      toast.success(`Change request sent to ${change.customer.email}`);
      setChange(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send change request");
    } finally {
      setSendingChange(false);
    }
  };

  const printManifest = () => {
    const assigned = bookings.filter((b) => b.vehicle_id);
    if (assigned.length === 0) return toast.error("Assign guests to vehicles first");
    const sortedVehicles = [...vehicles].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    let body = "";
    sortedVehicles.forEach((v) => {
      const pax = assigned.filter((b) => b.vehicle_id === v.id);
      if (pax.length === 0) return;
      const used = pax.reduce((s, b) => s + guestsOf(b), 0);
      const cap = v.capacity ? ` · ${used}/${v.capacity} seats` : ` · ${used} guests`;
      body += `<section class="veh"><h2>${esc(v.name)} <span>${esc(v.time)} · ${esc(v.pickup_location || "Viking Café")}${cap}</span></h2>
        <table><thead><tr><th>#</th><th>Guest</th><th>Tour</th><th>Guests</th><th>Phone</th><th>Checked in</th></tr></thead><tbody>`;
      pax.forEach((b, i) => {
        body += `<tr><td>${i + 1}</td><td>${esc(b.customer.full_name)}</td><td>${esc(b.tour_name)}</td><td>${guestsOf(b)}</td><td>${esc(b.customer.phone)}</td><td>${b.checked_in ? "✓" : ""}</td></tr>`;
      });
      body += `</tbody></table></section>`;
    });
    const unassigned = bookings.filter((b) => !b.vehicle_id);
    if (unassigned.length > 0) {
      body += `<section class="veh unassigned"><h2>Unassigned <span>${unassigned.length} booking(s)</span></h2><table><thead><tr><th>#</th><th>Guest</th><th>Tour</th><th>Date</th><th>Phone</th></tr></thead><tbody>`;
      unassigned.forEach((b, i) => {
        body += `<tr><td>${i + 1}</td><td>${esc(b.customer.full_name)}</td><td>${esc(b.tour_name)}</td><td>${esc(formatDate(b.date))}</td><td>${esc(b.customer.phone)}</td></tr>`;
      });
      body += `</tbody></table></section>`;
    }
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Vehicle Manifest — Immersion Festival</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Helvetica,Arial,sans-serif;color:#111;margin:32px;}
        h1{font-size:22px;letter-spacing:1px;text-transform:uppercase;margin:0 0 4px}
        .sub{color:#666;font-size:12px;margin:0 0 24px}
        .veh{margin:0 0 26px;page-break-inside:avoid}
        .veh h2{font-size:16px;margin:0 0 8px;border-bottom:2px solid #111;padding-bottom:6px}
        .veh h2 span{font-weight:400;font-size:12px;color:#666;text-transform:none}
        .unassigned h2{border-color:#c0392b;color:#c0392b}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{text-align:left;padding:7px 8px;border-bottom:1px solid #ddd}
        th{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888}
        @media print{body{margin:12mm}}
      </style></head><body>
      <h1>Vehicle Manifest</h1>
      <p class="sub">Immersion Festival · Iceland · printed ${new Date().toLocaleString()}</p>
      ${body}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print the manifest");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const sendOperatorSummary = async () => {
    if (!opDate) return toast.error("Pick a date");
    if (!opEmail.includes("@")) return toast.error("Enter the operator's email");
    setSendingOp(true);
    try {
      const r = await api.post("/admin/operator-summary", { date: opDate, email: opEmail.trim() });
      setOpsReport({ type: "operator", data: r.data });
      if (r.data.failed > 0) toast.error(`Operator email failed — see the list`);
      else toast.success(`Sent ${r.data.bookings} booking(s) to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send to operator");
    } finally {
      setSendingOp(false);
    }
  };
  const resendOperator = () => sendOperatorSummary();

  return (
    <div data-testid="admin-operations" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-xl uppercase tracking-tighter">Immersion Festival</Link>
          <button data-testid="ops-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Bus className="text-sky-400" size={22} />
            <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl">Operations</h1>
          </div>
          <Link to="/admin" data-testid="back-to-dashboard" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2 transition-colors">
            <ArrowLeft size={15} /> Dashboard
          </Link>
        </div>
        <p className="text-gray-500 text-sm mb-10">Confirm tours with the operator, assign guests to vehicles &amp; departure times, then send everyone their pickup details. Move a tour and email the guest for approval.</p>

        {/* Vehicles */}
        <section className="mb-12" data-testid="vehicles-manager">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300 mb-4">Transport Vehicles</h2>
          <div className="flex flex-wrap gap-3 mb-5">
            {vehicles.length === 0 && <p className="text-gray-600 text-xs uppercase tracking-[0.15em]">No vehicles yet — add one below</p>}
            {vehicles.map((v) => {
              const used = seatsUsed(v.id);
              const over = v.capacity > 0 && used > v.capacity;
              return (
              <div key={v.id} data-testid={`vehicle-chip-${v.id}`} className="inline-flex items-center gap-3 border border-white/15 px-4 py-3">
                <Bus size={15} className="text-sky-400" />
                <span className="text-sm text-white">{v.name}</span>
                <span className="text-xs text-gray-500">{v.time}</span>
                <span data-testid={`vehicle-seats-${v.id}`} className={`text-xs font-bold ${over ? "text-[#e07a6c]" : "text-emerald-400"}`}>
                  {v.capacity > 0 ? `${used}/${v.capacity} seats` : `${used} seats`}
                </span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-gray-600">{v.pickup_location}</span>
                <button data-testid={`vehicle-delete-${v.id}`} onClick={() => removeVehicle(v)} className="text-gray-500 hover:text-[#e07a6c] transition-colors"><Trash2 size={14} /></button>
              </div>
            );})}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Vehicle name</label>
              <input data-testid="vehicle-name-input" value={newVehicle.name} onChange={(e) => setNewVehicle({ ...newVehicle, name: e.target.value })} placeholder="Bus 1" className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-40" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Departure time</label>
              <input data-testid="vehicle-time-input" value={newVehicle.time} onChange={(e) => setNewVehicle({ ...newVehicle, time: e.target.value })} placeholder="11:00" className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-28" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Seats (capacity)</label>
              <input data-testid="vehicle-capacity-input" type="number" min="0" value={newVehicle.capacity} onChange={(e) => setNewVehicle({ ...newVehicle, capacity: e.target.value })} placeholder="e.g. 16" className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-24" />
            </div>
            <button data-testid="vehicle-add-btn" onClick={addVehicle} disabled={addingVehicle} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 disabled:opacity-50">
              <Plus size={14} /> Add Vehicle
            </button>
          </div>
        </section>

        {/* Email the operator the day's schedule */}
        <section className="mb-12 border border-white/10 p-6" data-testid="operator-email-panel">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300 mb-1">Email Tour Operator</h2>
          <p className="text-gray-500 text-xs mb-5">Send the operator every paid tour booked for a chosen day (guest, time, vehicle, phone).</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Date</label>
              <select data-testid="operator-date" value={opDate} onChange={(e) => setOpDate(e.target.value)} className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-44">
                <option value="" className="bg-[#0C0C0E] text-gray-400">Select date…</option>
                {(cfg.dates || []).map((d) => <option key={d} value={d} className="bg-[#0C0C0E]">{formatDate(d)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Operator email</label>
              <input data-testid="operator-email" type="email" value={opEmail} onChange={(e) => setOpEmail(e.target.value)} placeholder="operator@example.com" className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-64" />
            </div>
            <button data-testid="operator-send" onClick={sendOperatorSummary} disabled={sendingOp} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 disabled:opacity-50">
              {sendingOp ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to Operator
            </button>
          </div>
        </section>

        {/* Send departure info */}
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-300">Booked Activities · {bookings.length}</h2>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="print-manifest" onClick={printManifest} className="border border-white/20 text-white hover:border-white/60 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2">
              <Printer size={14} /> Print Manifest
            </button>
            <button data-testid="send-departure-info" onClick={sendAll} disabled={sendingAll} className="bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 disabled:opacity-50">
              {sendingAll ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Info to All
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm" data-testid="operations-table">
            <thead className="bg-[#0C0C0E]">
              <tr className="text-left text-xs uppercase tracking-[0.15em] text-gray-500">
                <th className="py-4 px-5">Guest</th>
                <th className="py-4 px-5">Tour</th>
                <th className="py-4 px-5">Date / Time</th>
                <th className="py-4 px-5">Operator</th>
                <th className="py-4 px-5">Vehicle</th>
                <th className="py-4 px-5">Departure info</th>
                <th className="py-4 px-5 text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && <tr><td colSpan={7} className="py-10 px-5 text-center text-gray-600 uppercase tracking-[0.2em] text-xs">No paid activities yet</td></tr>}
              {bookings.map((b) => {
                const st = STATUS_META[b.operator_status || "pending"];
                const pc = b.pending_change;
                return (
                  <tr key={b.id} className="border-t border-white/10" data-testid={`ops-row-${b.id}`}>
                    <td className="py-4 px-5"><span className="text-white">{b.customer.full_name}</span><br /><span className="text-gray-500 text-xs">{b.customer.email}</span></td>
                    <td className="py-4 px-5 text-gray-300">{b.tour_name}</td>
                    <td className="py-4 px-5 text-gray-400">{formatDate(b.date)}{b.slot ? <><br /><span className="text-xs text-gray-500">{b.slot}</span></> : null}</td>
                    <td className="py-4 px-5">
                      <select data-testid={`operator-status-${b.id}`} value={b.operator_status || "pending"} onChange={(e) => setOps(b, { operator_status: e.target.value })} className={`bg-transparent border py-2 px-2 text-xs uppercase tracking-[0.1em] focus:outline-none ${st.cls}`}>
                        <option value="pending" className="bg-[#0C0C0E] text-white">Pending</option>
                        <option value="sent" className="bg-[#0C0C0E] text-white">Sent to operator</option>
                        <option value="confirmed" className="bg-[#0C0C0E] text-white">Confirmed</option>
                      </select>
                    </td>
                    <td className="py-4 px-5">
                      <select data-testid={`vehicle-select-${b.id}`} value={b.vehicle_id || ""} onChange={(e) => setOps(b, { vehicle_id: e.target.value })} className="bg-transparent border border-white/15 py-2 px-2 text-xs text-white focus:outline-none focus:border-white">
                        <option value="" className="bg-[#0C0C0E] text-gray-400">— Unassigned —</option>
                        {vehicles.map((v) => {
                          const used = seatsUsed(v.id);
                          const seats = v.capacity > 0 ? ` (${used}/${v.capacity})` : "";
                          return <option key={v.id} value={v.id} className="bg-[#0C0C0E] text-white">{v.name} · {v.time}{seats}</option>;
                        })}
                      </select>
                      {b.vehicle_id && <div className="text-[10px] uppercase tracking-[0.1em] text-gray-600 mt-1">{b.pickup_location || "Viking Café"}</div>}
                    </td>
                    <td className="py-4 px-5">
                      {b.departure_info_sent
                        ? <span className="text-xs uppercase tracking-[0.1em] text-emerald-400">Sent</span>
                        : <span className="text-xs uppercase tracking-[0.1em] text-gray-600">Not sent</span>}
                    </td>
                    <td className="py-4 px-5 text-right">
                      {pc && pc.status === "proposed" && <div className="text-[10px] uppercase tracking-[0.1em] text-amber-400 mb-1">Awaiting reply</div>}
                      {pc && pc.status === "approved" && <div className="text-[10px] uppercase tracking-[0.1em] text-emerald-400 mb-1">Change approved</div>}
                      {pc && pc.status === "declined" && <div className="text-[10px] uppercase tracking-[0.1em] text-[#e07a6c] mb-1">Change declined</div>}
                      <button data-testid={`propose-change-${b.id}`} onClick={() => openChange(b)} className="text-gray-400 hover:text-white transition-colors inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em]">
                        <CalendarClock size={14} /> Move
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Propose change modal */}
      {change && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="change-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setChange(null)} />
          <div className="relative bg-[#0A0A0C] border border-white/10 w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setChange(null)} className="absolute top-5 right-5 text-gray-500 hover:text-white"><X size={18} /></button>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">Move Tour · Request Approval</p>
            <h3 className="font-display font-extrabold text-2xl mb-1">{change.tour_name}</h3>
            <p className="text-gray-500 text-sm mb-6">{change.customer.full_name} · {change.customer.email}</p>

            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">New date</label>
                <select data-testid="change-date" value={changeForm.date} onChange={(e) => setChangeForm({ ...changeForm, date: e.target.value })} className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-full">
                  {(cfg.dates || []).map((d) => <option key={d} value={d} className="bg-[#0C0C0E]">{formatDate(d)}</option>)}
                </select>
              </div>
              {change.type === "buggy" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">New time slot</label>
                  <select data-testid="change-slot" value={changeForm.slot} onChange={(e) => setChangeForm({ ...changeForm, slot: e.target.value })} className="bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-white w-full">
                    {(cfg.buggy_slots || []).map((s) => <option key={s} value={s} className="bg-[#0C0C0E]">{s}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="text-xs text-gray-400 mb-5 border border-white/10 p-3">
              <span className="text-gray-600">Currently:</span> {formatDate(change.date)}{change.slot ? ` · ${change.slot}` : ""}
              <span className="mx-2 text-gray-600">→</span>
              <span className="text-white">{formatDate(changeForm.date)}{change.type === "buggy" ? ` · ${changeForm.slot}` : ""}</span>
            </div>

            <label className="block text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-2">Message to guest (edit before sending)</label>
            <textarea data-testid="change-message" rows={5} value={changeForm.message} onChange={(e) => setChangeForm({ ...changeForm, message: e.target.value })} className="bg-transparent border border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-white w-full resize-none mb-2" />
            <p className="text-[11px] text-gray-600 mb-6">The email will include Approve / Decline buttons. Approving updates the booking automatically and resends the guest an updated confirmation with a new QR pass.</p>

            <div className="flex gap-3">
              <button data-testid="change-cancel" onClick={() => setChange(null)} className="flex-1 border border-white/20 text-gray-300 hover:border-white/50 uppercase tracking-[0.15em] text-xs font-bold py-3 transition-colors">Cancel</button>
              <button data-testid="change-send" onClick={sendChange} disabled={sendingChange} className="flex-1 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-3 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {sendingChange ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {opsReport && (
        <div data-testid="ops-report-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpsReport(null)}>
          <div className="bg-[#0C0C0E] border border-white/15 w-full max-w-lg max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-1">Email Delivery Report</p>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl">{opsReport.type === "operator" ? "Operator schedule email" : "Departure info emails"}</h3>
              </div>
              <button data-testid="ops-report-close" onClick={() => setOpsReport(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="flex gap-3 mb-5">
              <div className="flex-1 border border-emerald-500/25 bg-emerald-950/20 p-3 text-center"><p className="text-2xl font-bold text-emerald-400">{opsReport.data.sent}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">Sent</p></div>
              <div className="flex-1 border border-[#C44536]/30 bg-[#C44536]/10 p-3 text-center"><p className="text-2xl font-bold text-[#e07a6c]">{opsReport.data.failed}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">Failed</p></div>
              <div className="flex-1 border border-white/10 p-3 text-center"><p className="text-2xl font-bold text-gray-400">{opsReport.data.skipped}</p><p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-1">No email</p></div>
            </div>

            <div className="border border-white/10 divide-y divide-white/5 mb-5">
              {(opsReport.data.results || []).map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                  {r.status === "sent" ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" /> : r.status === "failed" ? <XCircle size={16} className="text-[#e07a6c] shrink-0" /> : <MinusCircle size={16} className="text-gray-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{r.name}</p>
                    <p className="text-xs text-gray-500 truncate">{r.email}{r.reason ? ` · ${r.reason}` : ""}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${r.status === "sent" ? "text-emerald-400" : r.status === "failed" ? "text-[#e07a6c]" : "text-gray-500"}`}>{r.status === "skipped" ? "No email" : r.status}</span>
                </div>
              ))}
              {(opsReport.data.results || []).length === 0 && <p className="px-4 py-6 text-center text-gray-600 text-xs uppercase tracking-[0.2em]">No recipients</p>}
            </div>

            {opsReport.data.failed > 0 && (
              <button data-testid="ops-resend-failed" onClick={opsReport.type === "operator" ? resendOperator : resendDepartureFailed} disabled={sendingAll || sendingOp} className="w-full uppercase tracking-[0.15em] text-xs font-bold py-3.5 bg-[#C44536] text-white hover:bg-[#d1543f] transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Send size={14} /> {(sendingAll || sendingOp) ? "Resending…" : opsReport.type === "operator" ? "Resend to operator" : `Resend to ${opsReport.data.failed} failed only`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
